<script setup>
import { computed } from "vue";
import KanbanActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import { isPlanEditable } from "../utils/planStatus";
import {
  compactClockLabel,
  formatAlertsTooltipHtml,
  formatSecondsAsMinutes,
  getAvatarText,
  normalizeHexColor,
  toSoftColorFromHex,
} from "../../../utils/formatters";
import { toSafeString } from "../../../utils/strings";

const props = defineProps({
  boardId: { type: String, required: true },
  title: { type: String, default: "" },
  accentColor: { type: String, default: "" },
  vehicleId: { type: [String, Number], default: "" },
  vehicleOptions: {
    type: Array,
    default() {
      return [];
    },
  },
  metrics: {
    type: Array,
    default() {
      return [];
    },
  },
  actionIcon: { type: String, default: "fas fa-ellipsis-h" },
  actionLabel: { type: String, default: "Acciones" },
  actions: {
    type: Array,
    default() {
      return [];
    },
  },
  progressValue: { type: Number, default: 0 },
  isSelectable: { type: Boolean, default: false },
  showMetrics: { type: Boolean, default: true },
  showProgress: { type: Boolean, default: true },
  showAction: { type: Boolean, default: true },
  detailOnDblclick: { type: Boolean, default: false },
  planStatus: { type: String, default: "" },
  selectableTitle: { type: String, default: "Añadir Vehículo" },
  selectableVehicles: {
    type: Array,
    default() {
      return [];
    },
  },
});

const emit = defineEmits(["update:vehicleId", "action", "detail"]);

function formatMetricTimeWindow(value) {
  const text = toSafeString(value);
  if (!text) return "";
  return text
    .replace(/(\d{1,2}:\d{2})/g, (match) => compactClockLabel(match))
    .replace(/\s*-\s*/g, " - ");
}

function getSelectableVehicleLabel(option) {
  return toSafeString(option?.label) || "Vehículo";
}

function getSelectableVehicleAvatar(option) {
  return getAvatarText(getSelectableVehicleLabel(option), option?.abbreviation);
}

function getSelectableVehicleStyle(option) {
  const accentColor = normalizeHexColor(option?.color, "#5baee9");
  return {
    "--selectable-vehicle-accent": accentColor,
    "--selectable-vehicle-soft": toSoftColorFromHex(accentColor, 0.16),
  };
}

const clampedProgress = computed(() =>
  Math.round(Math.max(0, Math.min(100, props.progressValue)))
);
const actionsMenuId = computed(
  () =>
    `board-actions-${String(props.boardId || "unknown").replace(
      /[^a-zA-Z0-9_-]/g,
      "_"
    )}`
);
const boardActions = computed(() => {
  if (Array.isArray(props.actions) && props.actions.length > 0) {
    return props.actions;
  }
  return [[{ label: "Eliminar", event: "deleteRoute", type: "danger" }]];
});

const selectedVehicleOption = computed(
  () =>
    props.vehicleOptions.find(
      (option) => toSafeString(option?.value) === toSafeString(props.vehicleId)
    ) || null
);
const selectedLabel = computed(
  () => selectedVehicleOption.value?.label || props.title || ""
);
const selectableHelperLabel = computed(() =>
  props.isSelectable ? props.selectableTitle : ""
);
const isEditable = computed(() => isPlanEditable(props.planStatus));
const accent = computed(() => props.accentColor || "#5baee9");
const warningMetric = computed(() =>
  props.metrics.find((metric) => metric?.icon === "fas fa-exclamation-circle")
);
const hasWarning = computed(() => Boolean(warningMetric.value));
const warningText = computed(
  () => formatAlertsTooltipHtml(warningMetric.value?.value) || "Advertencia"
);
const displayMetrics = computed(() =>
  props.metrics
    .filter((metric) => metric?.icon !== "fas fa-exclamation-circle")
    .map((metric) => {
      if (metric?.icon !== "fas fa-stopwatch") return metric;
      return {
        ...metric,
        value: formatSecondsAsMinutes(metric?.value, "0 min"),
      };
    })
);
const totalTimeMetric = computed(
  () =>
    displayMetrics.value.find(
      (metric) => metric?.icon === "fas fa-stopwatch"
    ) || null
);
const compactMetrics = computed(() =>
  displayMetrics.value
    .filter((metric) => metric?.icon !== "fas fa-stopwatch")
    .map((metric) => {
      if (metric?.icon === "fas fa-route") {
        const value = toSafeString(metric?.value);
        return { ...metric, value: value ? `${value} km` : "0 km" };
      }
      if (metric?.icon === "fas fa-clock") {
        return { ...metric, value: formatMetricTimeWindow(metric?.value) };
      }
      return metric;
    })
);
const progressAccent = computed(() => toSoftColorFromHex(accent.value, 0.65));
const headerStyle = computed(() => ({
  "--vehicle-accent": accent.value,
}));
const isDetailClickable = computed(
  () => props.detailOnDblclick && !props.isSelectable
);

function onBoardAction(action) {
  emit("action", action);
}

function onHeaderDoubleClick(event) {
  if (!props.detailOnDblclick || props.isSelectable) return;
  if (event?.target?.closest?.(".route-action-slot")) return;
  emit("detail");
}

function onSelectableVehicleClick(vehicleId) {
  emit("update:vehicleId", vehicleId);
}

</script>

<template>
  <div
    class="kanban-board-header"
    :class="{
      'has-warning': hasWarning,
      'is-detail-clickable': isDetailClickable,
      'is-selectable-card': isSelectable,
    }"
    :style="headerStyle"
    @dblclick="onHeaderDoubleClick"
  >
    <div v-if="showAction" class="route-action-slot">
      <kanban-actions-dropdown
        v-if="showAction"
        class="route-menu-wrap"
        :menu-id="actionsMenuId"
        :actions="boardActions"
        :aria-label="actionLabel"
        :icon-class="actionIcon"
        trigger-class="route-actions-button"
        menu-class="route-menu-dropdown"
        hover-reveal-scope=".kanban-board-header"
        @action="onBoardAction"
      />
    </div>

    <div class="route-top-row">
      <div class="route-select-wrap" :class="{ 'is-selectable': isSelectable }">
        <div
          v-if="isSelectable && isEditable"
          class="route-selectable-header-block"
        >
          <div class="route-selectable-title-row">
            <span class="route-title route-selectable-title mb-1">
              + {{ selectableHelperLabel }}
            </span>
          </div>
          <div class="route-selectable-list">
            <button
              v-for="vehicleOption in selectableVehicles"
              :key="vehicleOption.value"
              type="button"
              class="route-selectable-item"
              :style="getSelectableVehicleStyle(vehicleOption)"
              @click="onSelectableVehicleClick(vehicleOption.value)"
            >
              <span class="route-selectable-item-avatar">
                {{ getSelectableVehicleAvatar(vehicleOption) }}
              </span>
              <span class="route-selectable-item-label">
                {{ getSelectableVehicleLabel(vehicleOption) }}
              </span>
            </button>
          </div>
        </div>

        <div v-else class="route-name-pill">
          <div class="route-summary">
            <div class="route-text">
              <div class="route-title-row">
                <p class="route-title mb-0">
                  {{ selectedLabel || "Sin vehículo" }}
                </p>
                <div v-if="totalTimeMetric" class="route-total-time">
                  {{ totalTimeMetric.value }}
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="showMetrics && !isSelectable && compactMetrics.length"
            class="route-metrics"
          >
            <div class="metrics-row">
              <div
                v-for="(metric, idx) in compactMetrics"
                :key="`${boardId}-primary-${idx}`"
                class="metric-chip"
              >
                <template v-if="metric.icon === 'fas fa-map-marker-alt'">
                  <i :class="metric.icon" aria-hidden="true"></i>
                  <span class="metric-count">{{ metric.value }}</span>
                </template>
                <span v-else>{{ metric.value }}</span>
              </div>
              <div v-if="hasWarning" class="metric-chip-trailing-group">
                <div
                  v-tooltip="warningText"
                  class="metric-chip metric-chip-warning"
                  aria-label="Advertencia"
                >
                  <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showProgress && !isSelectable" class="route-progress-row">
      <div class="progress progress-sm route-progress">
        <div
          class="progress-bar"
          role="progressbar"
          :aria-valuenow="progressValue"
          aria-valuemin="0"
          aria-valuemax="100"
          :style="{
            width: `${Math.max(0, Math.min(100, progressValue))}%`,
            backgroundColor: progressAccent,
          }"
        ></div>
      </div>
      <span class="route-progress-text">{{ clampedProgress }}%</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "./planner-header-shared";
@use "./planner-surface-shared";

.kanban-board-header {
  @extend .planner-soft-surface;
  border: 1px solid var(--planner-border, #dbe2ee);
  border-radius: var(--planner-header-card-radius, 12px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: var(--planner-header-card-min-height, 68px);
  padding: var(--planner-header-card-padding, 6px 24px 8px 8px);
  position: relative;
  user-select: none;
}

.kanban-board-header.is-selectable-card {
  justify-content: flex-start;
}

.kanban-board-header.is-detail-clickable {
  cursor: pointer;
}

.kanban-board-header:focus-within {
  z-index: 30;
}

.kanban-board-header.kanban-dropdown-open {
  z-index: 220;
}

.route-select-wrap {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 1px;
  min-width: 0;
  overflow: hidden;
  position: relative;
  width: 0;
}

.route-select-wrap.is-selectable {
  align-items: stretch;
  overflow: visible;
  width: 100%;
}

.route-selectable-header-block {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 0;
}

.route-selectable-title-row {
  display: flex;
  justify-content: center;
  width: 100%;
}

.route-selectable-title {
  color: var(--color-text-primary) !important;
  text-align: center;
  width: 100%;
}

.route-selectable-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.route-selectable-item {
  align-items: center;
  background: var(--planner-surface, #fff);
  border: 1px solid rgba(203, 213, 225, 0.82);
  border-radius: 10px;
  display: flex;
  gap: 8px;
  min-height: 32px;
  min-width: 0;
  padding: 6px 8px;
  text-align: left;
  transition: border-color 0.18s ease, background-color 0.18s ease,
    transform 0.18s ease;
  width: 100%;
}

.route-selectable-item:hover {
  background: color-mix(
    in srgb,
    var(--selectable-vehicle-soft, rgba(91, 174, 233, 0.16)) 28%,
    white
  );
  border-color: color-mix(
    in srgb,
    var(--selectable-vehicle-accent, #5baee9) 34%,
    rgba(203, 213, 225, 0.82)
  );
  transform: translateY(-1px);
}

.route-selectable-item-avatar {
  align-items: center;
  background: var(--selectable-vehicle-soft, rgba(91, 174, 233, 0.16));
  border: 1px solid color-mix(
    in srgb,
    var(--selectable-vehicle-accent, #5baee9) 24%,
    white
  );
  border-radius: 999px;
  color: var(--selectable-vehicle-accent, #5baee9);
  display: inline-flex;
  flex: 0 0 20px;
  font-size: 0.60rem;
  font-weight: 600;
  height: 20px;
  justify-content: center;
  line-height: 1;
  text-transform: uppercase;
  width: 20px;
}

.route-selectable-item-label {
  color: var(--planner-text, var(--color-text-primary));
  font-size: 0.7rem;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.route-action-slot {
  @extend .planner-action-slot;
  position: absolute;
  right: 6px;
  top: 6px;
}

.route-menu-wrap {
  @extend .planner-action-wrap;
}

.route-menu-dropdown {
  @extend .planner-action-dropdown;
}

.route-progress-text {
  color: var(--color-text-secondary);
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}

.route-progress-row {
  align-items: center;
  display: flex;
  gap: 6px;
  margin-top: 6px;
  min-height: 12px;
}

.route-progress {
  background: rgba(203, 213, 225, 0.55);
  border-radius: 999px;
  flex: 1;
  height: 5px;
  margin: 0;
  overflow: hidden;
}

.route-total-time {
  @extend .planner-meta-time;
}

.metric-count {
  min-width: 0;
}
</style>
